import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, UserPlus, PhoneCall } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../store/authStore';
import { useSuccessToast } from '../../store/successToastStore';
import { InputField } from '../../components/InputField';
import { updateProfile, updateEmergencyContact, getMe } from '../../api/auth';
interface EditProfileProps {
  isPanel?: boolean;
}
export function EditProfile({
  isPanel = false
}: EditProfileProps) {
  const navigate = useNavigate();
  const {
    user,
    token,
    updateUser
  } = useAuth();
  const {
    showToast
  } = useSuccessToast();
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    emergencyContactName: (user as any)?.emergencyContactName || '',
    emergencyContactPhone: (user as any)?.emergencyContactPhone || ''
  });
  // Baseline used for change detection (updated when fresh data is loaded)
  const [baseline, setBaseline] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    emergencyContactName: (user as any)?.emergencyContactName || '',
    emergencyContactPhone: (user as any)?.emergencyContactPhone || ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDiscardSheet, setShowDiscardSheet] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Pre-populate emergency contact fields from the server on mount
  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    getMe(token).then(me => {
      if (!isMounted || !me) return;
      const next = {
        fullName: me.fullName ?? user?.fullName ?? '',
        email: me.email ?? user?.email ?? '',
        phone: me.phone ?? user?.phone ?? '',
        emergencyContactName: me.emergencyContactName ?? '',
        emergencyContactPhone: me.emergencyContactPhone ?? ''
      };
      setFormData(next);
      setBaseline(next);
    }).catch(() => {
      // Silently fall back to the values already in the auth store
    });
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  // Check for changes
  useEffect(() => {
    const changed = formData.fullName !== baseline.fullName || formData.email !== baseline.email || formData.phone !== baseline.phone || formData.emergencyContactName !== baseline.emergencyContactName || formData.emergencyContactPhone !== baseline.emergencyContactPhone;
    setHasChanges(changed);
  }, [formData, baseline]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async () => {
    if (!validate() || !hasChanges || !token) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const updatedUser = await updateProfile(token, {
        fullName: formData.fullName,
        phone: formData.phone
      });
      await updateEmergencyContact(token, {
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone
      });
      updateUser({
        ...updatedUser,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone
      });
      setBaseline(prev => ({
        ...prev,
        fullName: formData.fullName,
        phone: formData.phone,
        emergencyContactName: formData.emergencyContactName,
        emergencyContactPhone: formData.emergencyContactPhone
      }));
      showToast('Profile updated successfully');
      if (!isPanel) {
        navigate('/app/profile');
      }
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };
  const handleBack = () => {
    if (hasChanges) {
      setShowDiscardSheet(true);
    } else {
      navigate('/app/profile');
    }
  };
  const content = <main className="w-full max-w-[600px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-1914" data-mp="7hanwb5s9j1v">
      {/* Header */}
      {!isPanel && <div className="flex items-center justify-between mb-8" data-id="element-1915" data-mp="3racslut51py1">
          <div className="flex items-center" data-id="element-1916" data-mp="3r91sq62td2c7">
            <button onClick={handleBack} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-2" aria-label="Go back" data-id="element-1917" data-mp="3marxjvh3a1ql">
              <ArrowLeft size={24} className="text-text-primary" data-id="element-1918" data-mp="iu1a02pgr615" />
            </button>
            <h1 className="text-[20px] font-semibold text-text-primary" data-id="element-1919" data-mp="dwrnovxdfahj">
              Edit Profile
            </h1>
          </div>

          <button onClick={handleSave} disabled={!hasChanges || isSaving} className={`text-[15px] font-semibold transition-colors ${hasChanges ? 'text-primary' : 'text-text-secondary opacity-50'}`} data-id="element-1920" data-mp="9vzc3w110ti3">
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>}

      {isPanel && <div className="flex items-center justify-between mb-8" data-id="element-1921" data-mp="2r1wkp2l6h1nq">
          <h2 className="text-[24px] font-semibold text-text-primary" data-id="element-1922" data-mp="2w197jnc1u8qb">
            Edit Profile
          </h2>
          <button onClick={handleSave} disabled={!hasChanges || isSaving} className={`px-4 py-2 rounded-pill text-[14px] font-semibold transition-colors ${hasChanges ? 'bg-primary text-white' : 'bg-secondary text-text-secondary cursor-not-allowed'}`} data-id="element-1923" data-mp="2i1rpdqt9dtpx">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>}

      {/* Error Banner */}
      {saveError && <div className="bg-danger/10 border border-danger/20 rounded-[12px] p-3 mb-6 flex items-center justify-between animate-slideDown" data-id="element-1924" data-mp="1yf3jkj3vm0d7">
          <span className="text-[14px] text-danger font-medium" data-id="element-1925" data-mp="1pcu8ns4p44za">
            {saveError}
          </span>
          <button onClick={() => setSaveError(null)} className="text-danger text-[13px] font-semibold ml-4" data-id="element-1926" data-mp="1ubnpnuj6ye47">
            Dismiss
          </button>
        </div>}

      {/* Form */}
      <div className="bg-white rounded-[24px] p-6 shadow-card" data-id="element-1927" data-mp="15ru3zsv3g5h0">
        <div className="space-y-6" data-id="element-1928" data-mp="1jqtr2w2ykcb2">
          <div data-id="element-1929" data-mp="1jr22ejjfn9k5">
            <label htmlFor="fullName" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1930" data-mp="1jrlbdar3unm1">
              Full Name
            </label>
            <InputField id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Enter your full name" icon={<User size={20} data-id="element-1932" data-mp="23e9luvrfn553" />} error={errors.fullName} className="mb-0" data-id="element-1931" data-mp="1tny5uauh55hu" />
          </div>

          <div data-id="element-1933" data-mp="1yi2ecyje1ll1">
            <label htmlFor="email" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1934" data-mp="2cf7p3bu1r8mm">
              Email Address
            </label>
            <InputField id="email" name="email" type="email" value={formData.email} readOnly disabled placeholder="Enter your email" icon={<Mail size={20} data-id="element-1936" data-mp="3nul5d0ziy23e" />} className="mb-1 opacity-70 cursor-not-allowed" data-id="element-1935" data-mp="3esty3rpc5n7f" />
            <p className="text-[12px] text-text-secondary ml-1" data-id="element-1937" data-mp="3srbjhirrqnm6">
              Email cannot be changed
            </p>
          </div>

          <div data-id="element-1938" data-mp="2w09c8u820id4">
            <label htmlFor="phone" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1939" data-mp="303xjtcwordyd">
              Phone Number (optional)
            </label>
            <InputField id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="Enter your phone number" icon={<Phone size={20} data-id="element-1941" data-mp="2hceg13uu7vva" />} error={errors.phone} className="mb-0" data-id="element-1940" data-mp="39tqt279cvgzg" />
          </div>

          <div className="h-[1px] w-full bg-border" data-id="element-1942" data-mp="29t5hy6rs7jlj" />

          <div data-id="element-1943" data-mp="24uc0k39dj8g3">
            <h3 className="text-[15px] font-semibold text-text-primary mb-1" data-id="element-1944" data-mp="24vvli75z67o7">
              Emergency Contact
            </h3>
            <p className="text-[13px] text-text-secondary" data-id="element-1945" data-mp="1ztxv8sz5qclr">
              Who we should reach in case of an emergency. Optional.
            </p>
          </div>

          <div data-id="element-1946" data-mp="1qvccoxtoqxkz">
            <label htmlFor="emergencyContactName" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1947" data-mp="1qvuiom65gq63">
              Emergency Contact Name (optional)
            </label>
            <InputField id="emergencyContactName" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleChange} placeholder="Enter a contact name" icon={<UserPlus size={20} data-id="element-1949" data-mp="1358cz1jmbhz9" />} error={errors.emergencyContactName} className="mb-0" data-id="element-1948" data-mp="1h36ntwyqcwa0" />
          </div>

          <div data-id="element-1950" data-mp="25i4qfmfwze6d">
            <label htmlFor="emergencyContactPhone" className="block text-[14px] font-medium text-text-primary mb-2" data-id="element-1951" data-mp="20l4j787bw5do">
              Emergency Contact Phone (optional)
            </label>
            <InputField id="emergencyContactPhone" name="emergencyContactPhone" type="tel" value={formData.emergencyContactPhone} onChange={handleChange} placeholder="Enter a contact phone number" icon={<PhoneCall size={20} data-id="element-1953" data-mp="1wj9m6fwfke5p" />} error={errors.emergencyContactPhone} className="mb-0" data-id="element-1952" data-mp="2aaeeewxgy460" />
          </div>
        </div>
      </div>

      {/* Discard Changes Bottom Sheet */}
      <AnimatePresence data-id="element-1954" data-mp="37xwtephq42ig">
        {showDiscardSheet && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowDiscardSheet(false)} className="fixed inset-0 bg-black/40 z-[100]" data-id="element-1955" data-mp="32zli0abs5jy4" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[24px] p-6 z-[101] pb-[env(safe-area-inset-bottom)]" data-id="element-1956" data-mp="31k93dsw1pbf0">
              <div className="w-[40px] h-[4px] bg-border rounded-full mx-auto mb-6" data-id="element-1957" data-mp="3oh2oqcl8ilyv" />
              <div className="text-center" data-id="element-1958" data-mp="g1iy7lby7hni">
                <h2 className="text-[18px] font-semibold text-text-primary mb-2" data-id="element-1959" data-mp="ktsm6vti67n5">
                  Discard changes?
                </h2>
                <p className="text-[15px] text-text-secondary mb-8" data-id="element-1960" data-mp="uoreh0k46d6c">
                  You have unsaved changes. Are you sure you want to go back?
                </p>

                <button onClick={() => navigate('/app/profile')} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium text-[16px] mb-3" data-id="element-1961" data-mp="loukcjeygm3w">
                  Discard
                </button>
                <button onClick={() => setShowDiscardSheet(false)} className="w-full h-[52px] bg-transparent text-text-secondary rounded-[14px] font-medium text-[16px]" data-id="element-1962" data-mp="bt1aj7smf6pt">
                  Keep Editing
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
  return content;
}